import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { apiClient } from "~/api/ApiClient";
import Button from "~/components/mantine/button/Button";
import Breadcrumbs from "~/components/ui/breadcrumbs/Breadcrumbs";
import { AlertCard } from "~/components/ui/card/AlertCard";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { FEATURE_FLAGS, useFeatureFlag } from "~/featureFlags";
import { messageFromError } from "~/helpers/messageFromError";
import { tryCatch } from "~/helpers/tryCatch";
import { useGoogleDashboard } from "~/hooks/useGoogleDashboard";
import { usePageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";

import { DEFAULT_DASHBOARD } from "./DEFAULT_DASHBOARD.const";
import styles from "./GoogleDashboardPage.module.scss";
import { GoogleDashboardPageSkeleton } from "./GoogleDashboardPageSkeleton";
import { DashboardPreview } from "./components/DashboardView/DashboardPreview";
import { DashboardView } from "./components/DashboardView/DashboardView";
import { QUERY_KEYS } from "./constants";

const GoogleDashboardPage = () => {
  usePageTitle(PAGE_TITLES.googleDashboard);

  const { id } = useParams();

  if (!id) throw new Error("Site ID is required");

  const siteId = parseInt(id);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // The Integrations tab (Google OAuth) is switched off on white-label
  // hosts, so there is nowhere to send the user to connect an account.
  const canConnectGoogle = useFeatureFlag(
    FEATURE_FLAGS.SITES_SETTINGS_GOOGLE_INTEGRATIONS,
  );

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async () => {
      await apiClient.sites.dashboardsCreate(siteId, DEFAULT_DASHBOARD);

      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.DASHBOARDS(siteId),
      });
    },
  });

  const createDashboard = async () => {
    const result = await tryCatch(mutateAsync());

    if (result.error) {
      enqueueSnackbar(messageFromError(result.error), {
        variant: "error",
        persist: true,
      });
    }
  };

  const {
    shouldShowGoogleDash,
    dashboards = [],
    integrations = [],
    isLoading: isGoogleDashboardLoading,
  } = useGoogleDashboard({
    siteId,
  });

  useEffect(() => {
    if (isGoogleDashboardLoading) return;
    if (shouldShowGoogleDash) return;

    navigate(routes.home(), { replace: true });
  }, [isGoogleDashboardLoading, shouldShowGoogleDash, navigate]);

  if (isGoogleDashboardLoading) {
    return <GoogleDashboardPageSkeleton />;
  }

  if (!shouldShowGoogleDash) return null;

  const hasGoogleAnalyticsIntegration = integrations.some(
    (integration) => integration.kind === "google_analytics",
  );

  if (dashboards.length > 0) {
    return <DashboardView siteId={siteId} dashboard={dashboards[0]} />;
  }

  // Plan allows the feature but the user hasn't completed setup yet —
  // render a blurred dashboard preview with an inline AlertCard pointing
  // them to the next step (Google Analytics integration → first dashboard).
  const breadcrumbs = [
    { title: PAGE_TITLES.dashboard, link: routes.home() },
    {
      title: PAGE_TITLES.googleDashboard,
      link: routes.site.googleDashboard({ id: String(siteId) }),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.wrapper}>
        <div>
          <h1 className={styles.title}>{PAGE_TITLES.googleDashboard}</h1>
          <Breadcrumbs fakeBreadcrumbs={breadcrumbs} />
        </div>
      </div>

      <AlertCard
        type="neutral"
        className={styles.previewAlert}
        title={
          hasGoogleAnalyticsIntegration
            ? "Create your first Google Dashboard"
            : "Connect Google Analytics to unlock your dashboard"
        }
        content={
          hasGoogleAnalyticsIntegration ? (
            <>
              You&apos;re all set on the integration side. Create your first
              dashboard to start tracking organic sessions, top landing pages,
              search queries, and engagement trends from Google Analytics and
              Search Console.
            </>
          ) : (
            <>
              The Google Dashboard pulls live metrics from Google Analytics and
              Search Console — organic sessions, top landing pages, search
              queries, and engagement trends.{" "}
              {canConnectGoogle
                ? "Connect your Google account in Site Settings to start seeing real data here."
                : "Google Analytics hasn't been connected for this site yet."}
            </>
          )
        }
        actions={
          hasGoogleAnalyticsIntegration ? (
            <Button onClick={createDashboard} loading={isPending}>
              Create my first dashboard
            </Button>
          ) : canConnectGoogle ? (
            <Button
              onClick={() =>
                navigate(
                  routes.site.settings.tab({
                    id: String(siteId),
                    tabId: "integrations",
                  }),
                )
              }
            >
              Connect Google Analytics
            </Button>
          ) : null
        }
      />
      <div className={styles.previewContent} aria-hidden>
        <DashboardPreview />
      </div>
    </div>
  );
};

export default GoogleDashboardPage;
